import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCategories, autoCategorize } from '../../data/categoriesStore';
import { getApiBaseUrl, setApiBaseUrl, resetApiBaseUrl, DEFAULT_API_URL } from '../../data/settingsStore';
import { apiService } from '../../services/api';
import { Layout } from '../../components/Layout/Layout';
import { api } from '../../services/api';
import {
  ProductDto,
  VariantDto,
  ProductsApiResponse,
  VariantsApiResponse,
  SearchReplaceItem,
} from '../../types';
import styles from './Produits.module.css';

export const Produits: React.FC = () => {
  const navigate = useNavigate();

  // États principaux
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [variants, setVariants] = useState<VariantDto[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedVariants, setSelectedVariants] = useState<VariantDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [logs, setLogs] = useState<string[]>([]);

  // Sélection pour sauvegarde ciblée
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());

  // Paramètre URL API
  const [showApiSettings, setShowApiSettings] = useState(false);
  const [currentApiUrl, setCurrentApiUrl] = useState(getApiBaseUrl());
  const [editingApiUrl, setEditingApiUrl] = useState(false);
  const [newApiUrl, setNewApiUrl] = useState('');

  // États pour tri
  const [sortBy, setSortBy] = useState<string>(() => sessionStorage.getItem('pf_sortBy') ?? '');

  // État pour filtre/recherche rapide
  const [filterQuery, setFilterQuery] = useState<string>(() => sessionStorage.getItem('pf_query') ?? '');
  const [filterCategory, setFilterCategory] = useState<string>(() => sessionStorage.getItem('pf_category') ?? '');
  const [filterSubCategory, setFilterSubCategory] = useState<string>(() => sessionStorage.getItem('pf_subCategory') ?? '');
  const [filterImage, setFilterImage] = useState<string>(() => sessionStorage.getItem('pf_image') ?? '');
  const [filterDesc, setFilterDesc] = useState<string>(() => sessionStorage.getItem('pf_desc') ?? '');
  const [filterMeta, setFilterMeta] = useState(() => sessionStorage.getItem('pf_meta') === '1');
  const [filterNoCategory, setFilterNoCategory] = useState(() => sessionStorage.getItem('pf_noCat') === '1');
  const [filterNoSubCategory, setFilterNoSubCategory] = useState(() => sessionStorage.getItem('pf_noSubCat') === '1');
  const [filterBrand, setFilterBrand] = useState<string>(() => sessionStorage.getItem('pf_brand') ?? '');
  const [filterDescQuery, setFilterDescQuery] = useState<string>(() => sessionStorage.getItem('pf_descQuery') ?? '');
  const [filterDuplicates, setFilterDuplicates] = useState(() => sessionStorage.getItem('pf_duplicates') === '1');
  const [ignoredDuplicateIds, setIgnoredDuplicateIds] = useState<Set<number>>(() => {
    try { return new Set<number>(JSON.parse(sessionStorage.getItem('pf_ignoredDups') ?? '[]')); } catch { return new Set<number>(); }
  });

  // Persistance des filtres dans sessionStorage
  useEffect(() => { sessionStorage.setItem('pf_sortBy', sortBy); }, [sortBy]);
  useEffect(() => { sessionStorage.setItem('pf_query', filterQuery); }, [filterQuery]);
  useEffect(() => { sessionStorage.setItem('pf_category', filterCategory); }, [filterCategory]);
  useEffect(() => { sessionStorage.setItem('pf_subCategory', filterSubCategory); }, [filterSubCategory]);
  useEffect(() => { sessionStorage.setItem('pf_image', filterImage); }, [filterImage]);
  useEffect(() => { sessionStorage.setItem('pf_desc', filterDesc); }, [filterDesc]);
  useEffect(() => { sessionStorage.setItem('pf_meta', filterMeta ? '1' : ''); }, [filterMeta]);
  useEffect(() => { sessionStorage.setItem('pf_noCat', filterNoCategory ? '1' : ''); }, [filterNoCategory]);
  useEffect(() => { sessionStorage.setItem('pf_noSubCat', filterNoSubCategory ? '1' : ''); }, [filterNoSubCategory]);
  useEffect(() => { sessionStorage.setItem('pf_brand', filterBrand); }, [filterBrand]);
  useEffect(() => { sessionStorage.setItem('pf_descQuery', filterDescQuery); }, [filterDescQuery]);
  useEffect(() => { sessionStorage.setItem('pf_duplicates', filterDuplicates ? '1' : ''); }, [filterDuplicates]);
  useEffect(() => { sessionStorage.setItem('pf_ignoredDups', JSON.stringify([...ignoredDuplicateIds])); }, [ignoredDuplicateIds]);

  const categoryList = useMemo(() => getCategories(), []);
  const subCategoryList = useMemo(
    () => categoryList.find((c) => c.name === filterCategory)?.subCategories ?? [],
    [categoryList, filterCategory]
  );

  // États pour recherche/remplacement
  const [searchText, setSearchText] = useState<string>('');
  const [replaceText, setReplaceText] = useState<string>('');
  const [replacePreview, setReplacePreview] = useState<SearchReplaceItem[]>([]);

  useEffect(() => {
    loadProductsWithVariants();
  }, []);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString('fr-FR');
    setLogs((prev) => [...prev, `${timestamp} - ${message}`]);
  };

  const loadProductsWithVariants = async () => {
    try {
      setLoading(true);
      setError('');
      addLog('🔹 Début du chargement des produits...');

      // Charger produits
      const responseProducts = await api.get<ProductsApiResponse>('products/admin');
      addLog(`📡 Réponse API produits - Status: ${responseProducts.status}`);

      const productsData = responseProducts.data?.data?.products || [];
      setProducts(productsData);
      addLog(`📦 Nombre de produits chargés: ${productsData.length}`);

      // Charger variantes
      const responseVariants = await api.get<VariantsApiResponse>('variants');
      const variantsData = responseVariants.data?.data || [];
      setVariants(variantsData);
      addLog(`🔧 Nombre de variantes chargées: ${variantsData.length}`);

      addLog('✅ Chargement terminé !');
    } catch (err: any) {
      const errorMsg = 'Erreur lors du chargement : ' + (err.message || 'Erreur inconnue');
      setError(errorMsg);
      addLog(`❌ ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (product: ProductDto) => {
    setSelectedProductId(product.id);
    const productVariants = variants.filter((v) => v.productId === product.id);
    setSelectedVariants(productVariants);
  };

  const handleSort = () => {
    if (!sortBy) {
      alert('Veuillez choisir un critère de tri.');
      return;
    }

    let sorted: ProductDto[] = [];

    switch (sortBy) {
      case 'name-asc':
        sorted = [...products].sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        sorted = [...products].sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'stock-asc':
        sorted = [...products].sort((a, b) => a.stock - b.stock);
        break;
      case 'stock-desc':
        sorted = [...products].sort((a, b) => b.stock - a.stock);
        break;
      default:
        return;
    }

    setProducts(sorted);
    addLog(`✅ Tri appliqué : ${sortBy}`);
  };

  const handleAdd = () => {
    navigate('/produits/nouveau');
  };

  const handleDelete = async (productId: number) => {
    const confirmed = window.confirm(
      `Voulez-vous vraiment supprimer le produit ID=${productId} ?`
    );
    if (!confirmed) return;

    try {
      await api.delete(`products/${productId}`);
      addLog(`✅ Produit ID=${productId} supprimé`);
      loadProductsWithVariants();
    } catch (err: any) {
      alert('Erreur lors de la suppression : ' + err.message);
    }
  };

  const handleSave = async () => {
    const toSave = checkedIds.size > 0
      ? products.filter((p) => checkedIds.has(p.id))
      : products;

    if (toSave.length === 0) {
      alert('Aucun produit à sauvegarder.');
      return;
    }

    try {
      addLog(`🟦 Sauvegarde de ${toSave.length} produit(s)...`);

      for (const product of toSave) {
        try {
          await api.put(`products/${product.id}`, product);
          addLog(`✅ Produit ID=${product.id} sauvegardé`);
        } catch (err: any) {
          addLog(`❌ Échec produit ID=${product.id} : ${err.message}`);
        }
      }

      addLog('✅ Sauvegarde terminée !');
      alert(`${toSave.length} produit(s) enregistré(s) !`);
      setCheckedIds(new Set());
      loadProductsWithVariants();
    } catch (err: any) {
      alert('Erreur lors de la sauvegarde : ' + err.message);
    }
  };

  const handleAutoCategorize = () => {
    const targets = checkedIds.size > 0
      ? products.filter((p) => checkedIds.has(p.id))
      : products;

    let changed = 0;
    const updated = products.map((p) => {
      if (!targets.find((t) => t.id === p.id)) return p;
      const result = autoCategorize(p.name);
      if (!result) return p;
      // N'écrase que si pas déjà catégorisé, ou si la case est cochée explicitement
      if (p.category && p.subCategory && checkedIds.size === 0) return p;
      changed++;
      setCheckedIds((prev) => new Set(prev).add(p.id));
      return { ...p, category: result.category, subCategory: result.subCategory };
    });

    setProducts(updated);
    addLog(`✅ Auto-catégorisation : ${changed} produit(s) mis à jour`);
    if (changed === 0) {
      alert('Aucun produit reconnu par les règles. Vérifiez les noms de produits.');
    } else {
      alert(`${changed} produit(s) catégorisé(s) automatiquement. Pensez à enregistrer.`);
    }
  };

  const handleCleanTitles = () => {
    let changed = 0;

    const cleanedProducts = products.map((p) => {
      const original = p.name;
      const lastDashIndex = original.lastIndexOf(' - ');

      if (lastDashIndex < 0) return p;

      const suffix = original.substring(lastDashIndex + 3).trim();

      // Supprimer "Default Title"
      if (suffix.toLowerCase() === 'default title') {
        changed++;
        return { ...p, name: original.substring(0, lastDashIndex).trim() };
      }

      // Supprimer si correspond à une variante
      const hasVariantName = variants.some(
        (v) =>
          v.productId === p.id &&
          v.name &&
          suffix.toLowerCase() === v.name.toLowerCase()
      );

      if (hasVariantName) {
        changed++;
        return { ...p, name: original.substring(0, lastDashIndex).trim() };
      }

      return p;
    });

    setProducts(cleanedProducts);
    addLog(`✅ ${changed} titres nettoyés`);
    alert(`${changed} titres nettoyés.`);
  };

  const handleUpdateStock = () => {
    const updatedProducts = products.map((product) => {
      const productVariants = variants.filter((v) => v.productId === product.id);

      if (productVariants.length === 0) {
        return product;
      }

      const totalStock = productVariants.reduce((sum, v) => sum + v.stock, 0);
      return { ...product, stock: totalStock };
    });

    setProducts(updatedProducts);
    addLog('✅ Stock mis à jour avec succès !');
    alert('✅ Stock mis à jour avec succès !');
  };

  const handleSearchReplacePreview = () => {
    if (!searchText.trim()) {
      alert('Veuillez entrer un texte à rechercher.');
      return;
    }

    const preview: SearchReplaceItem[] = [];

    // Produits
    products.forEach((p) => {
      if (p.name && p.name.toLowerCase().includes(searchText.toLowerCase())) {
        preview.push({
          Type: 'Produit',
          Id: p.id,
          Original: p.name,
          Updated: p.name.replace(new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), replaceText),
        });
      }
    });

    // Variantes
    variants.forEach((v) => {
      if (v.name && v.name.toLowerCase().includes(searchText.toLowerCase())) {
        preview.push({
          Type: 'Variante',
          Id: v.id,
          Original: v.name,
          Updated: v.name.replace(new RegExp(searchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), replaceText),
        });
      }
    });

    setReplacePreview(preview);
    addLog(`🔍 ${preview.length} éléments trouvés`);
  };

  const handleSearchReplaceApply = async () => {
    if (replacePreview.length === 0) {
      alert('Aucun élément à modifier.');
      return;
    }

    try {
      let updatedCount = 0;

      for (const item of replacePreview) {
        if (item.Type === 'Produit') {
          const product = products.find((p) => p.id === item.Id);
          if (product) {
            const updatedProduct = { ...product, name: item.Updated };
            await api.put(`products/${product.id}`, updatedProduct);
            updatedCount++;
          }
        } else if (item.Type === 'Variante') {
          const variant = variants.find((v) => v.id === item.Id);
          if (variant) {
            const updatedVariant = { ...variant, name: item.Updated };
            await api.put(`variants/${variant.id}`, updatedVariant);
            updatedCount++;
          }
        }
      }

      alert(`Terminé : ${updatedCount} éléments mis à jour.`);
      addLog(`✅ ${updatedCount} éléments mis à jour`);
      loadProductsWithVariants();
    } catch (err: any) {
      alert('Erreur : ' + err.message);
    }
  };

  const descHasTags = (desc: string | null | undefined): boolean => {
    if (!desc) return false;
    return /<[^>]+>/.test(desc) || desc.includes('\\n');
  };

  const cleanDesc = (desc: string): string => {
    let d = desc;
    // \n littéraux (la chaine "\\n") → saut de ligne réel
    d = d.replace(/\\n/g, '\n');
    // <br> → saut de ligne
    d = d.replace(/<br\s*\/?>/gi, '\n');
    // <p> ouvrant → rien, </p> → saut de ligne
    d = d.replace(/<p[^>]*>/gi, '').replace(/<\/p>/gi, '\n');
    // Toutes les autres balises
    d = d.replace(/<[^>]+>/g, '');
    // Entités HTML courantes
    d = d.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').replace(/&quot;/g, '"');
    // Sauts de ligne multiples → double saut max
    d = d.replace(/\n{3,}/g, '\n\n');
    return d.trim();
  };

  const handleCleanDescriptions = () => {
    const targets = checkedIds.size > 0
      ? products.filter((p) => checkedIds.has(p.id))
      : products;

    let changed = 0;
    const updated = products.map((p) => {
      if (!targets.find((t) => t.id === p.id)) return p;
      if (!descHasTags(p.description)) return p;
      changed++;
      setCheckedIds((prev) => new Set(prev).add(p.id));
      return { ...p, description: cleanDesc(p.description) };
    });

    setProducts(updated);
    addLog(`✅ ${changed} description(s) nettoyée(s)`);
    alert(`${changed} description(s) nettoyée(s). Pensez à enregistrer.`);
  };

  const handleEnrichir = () => {
    const STORAGE_KEY = 'enrichisseur_products';
    const targets = checkedIds.size > 0
      ? products.filter((p) => checkedIds.has(p.id))
      : filteredProducts.filter((p) => !p.image || !p.image.startsWith('https://eliquidesud.com/Images/img-produits/'));
    if (targets.length === 0) { alert('Aucun produit sélectionné ou sans image valide.'); return; }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(targets));
    navigate('/produits/enrichir');
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get<ProductsApiResponse>('products');
      const productsToExport = response.data?.data?.products || [];

      // Convertir en CSV simple
      const headers = Object.keys(productsToExport[0] || {});
      const csvContent =
        headers.join(',') +
        '\n' +
        productsToExport
          .map((p) =>
            headers.map((h) => `"${(p as any)[h] || ''}"`).join(',')
          )
          .join('\n');

      // Télécharger
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = 'produits_export.csv';
      link.click();
      URL.revokeObjectURL(url);

      addLog('✅ Export CSV terminé !');
    } catch (err: any) {
      alert('Erreur export : ' + err.message);
    }
  };

  const handleSaveApiUrl = () => {
    const trimmed = newApiUrl.trim();
    if (!trimmed) {
      alert('URL invalide.');
      return;
    }
    setApiBaseUrl(trimmed);
    apiService.updateBaseUrl(trimmed.endsWith('/') ? trimmed : trimmed + '/');
    const saved = trimmed.endsWith('/') ? trimmed : trimmed + '/';
    setCurrentApiUrl(saved);
    setEditingApiUrl(false);
    setNewApiUrl('');
    addLog(`✅ URL API mise à jour : ${saved}`);
  };

  const handleFieldChange = (productId: number, field: keyof ProductDto, value: any) => {
    setProducts((prev) =>
      prev.map((p) => (p.id === productId ? { ...p, [field]: value } : p))
    );
    // Auto-cocher le produit modifié
    setCheckedIds((prev) => new Set(prev).add(productId));
  };

  const handleToggleCheck = (productId: number) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      next.has(productId) ? next.delete(productId) : next.add(productId);
      return next;
    });
  };

  const getVariantCount = (productId: number): number => {
    return variants.filter((v) => v.productId === productId).length;
  };

  // ── Détection des doublons (DOIT être avant filteredProducts) ────────────────
  const { duplicateIds, duplicateGroups } = useMemo(() => {
    const normalize = (name: string) =>
      name.toLowerCase()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[-|\/\\,;:()[\]]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const ids = new Set<number>();
    const groups = new Map<number, number[]>();

    for (let i = 0; i < products.length; i++) {
      for (let j = i + 1; j < products.length; j++) {
        const idA = products[i].id;
        const idB = products[j].id;
        if (ignoredDuplicateIds.has(idA) || ignoredDuplicateIds.has(idB)) continue;

        const a = normalize(products[i].name);
        const b = normalize(products[j].name);
        if (!a || !b) continue;

        const wordsA = a.split(' ').filter(w => w.length >= 3);
        const wordsB = b.split(' ').filter(w => w.length >= 3);
        if (wordsA.length === 0 || wordsB.length === 0) continue;

        const setB = new Set(wordsB);
        const common = wordsA.filter(w => setB.has(w));
        const minWords = Math.min(wordsA.length, wordsB.length);
        const overlap = common.length / minWords;

        const shorter = a.length < b.length ? a : b;
        const longer  = a.length < b.length ? b : a;
        const isSubstring = longer.includes(shorter) && shorter.length / longer.length >= 0.45;
        const isSimilar   = overlap >= 0.8 && minWords >= 2;

        if (isSubstring || isSimilar) {
          ids.add(idA);
          ids.add(idB);
          groups.set(idA, [...(groups.get(idA) ?? []), idB]);
          groups.set(idB, [...(groups.get(idB) ?? []), idA]);
        }
      }
    }
    return { duplicateIds: ids, duplicateGroups: groups };
  }, [products, ignoredDuplicateIds]);

  const filteredProducts = products.filter((p) => {
    const q = filterQuery.toLowerCase();
    const matchesSearch =
      !filterQuery.trim() ||
      p.name.toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q) ||
      (p.subCategory || '').toLowerCase().includes(q) ||
      (p.brand || '').toLowerCase().includes(q);
    const matchesCat = !filterCategory || p.category === filterCategory;
    const matchesSub = !filterSubCategory || p.subCategory === filterSubCategory;
    const hasImage = !!p.image && p.image.trim() !== '';
    const isValidImage = hasImage && p.image.startsWith('https://eliquidesud.com/Images/img-produits/');
    const matchesImage =
      !filterImage ||
      (filterImage === 'no-image' && !hasImage) ||
      (filterImage === 'invalid-image' && hasImage && !isValidImage);
    const matchesDesc = !filterDesc || (filterDesc === 'has-tags' && descHasTags(p.description));
    const matchesMeta = !filterMeta || !p.metaDescription || p.metaDescription.trim() === '';
    const matchesNoCategory = !filterNoCategory || !p.category || p.category.trim() === '';
    const matchesNoSubCategory = !filterNoSubCategory || !p.subCategory || p.subCategory.trim() === '';
    const matchesBrand = !filterBrand || (p.brand || '').toLowerCase().includes(filterBrand.toLowerCase());
    const matchesDescQuery = !filterDescQuery.trim() || (p.description || '').toLowerCase().includes(filterDescQuery.toLowerCase());
    const matchesDuplicates = !filterDuplicates || duplicateIds.has(p.id);
    return matchesSearch && matchesCat && matchesSub && matchesImage && matchesDesc && matchesMeta && matchesNoCategory && matchesNoSubCategory && matchesBrand && matchesDescQuery && matchesDuplicates;
  });


  const knownBrands = useMemo(() => {
    const brands = new Set<string>();
    products.forEach(p => { if (p.brand?.trim()) brands.add(p.brand.trim()); });
    return Array.from(brands).sort();
  }, [products]);

  const allVisibleChecked =
    filteredProducts.length > 0 && filteredProducts.every((p) => checkedIds.has(p.id));

  const handleToggleAll = () => {
    if (allVisibleChecked) {
      setCheckedIds((prev) => {
        const next = new Set(prev);
        filteredProducts.forEach((p) => next.delete(p.id));
        return next;
      });
    } else {
      setCheckedIds((prev) => {
        const next = new Set(prev);
        filteredProducts.forEach((p) => next.add(p.id));
        return next;
      });
    }
  };

  return (
    <Layout>
      <div className={styles.container}>
        <h1 className={styles.title}>Gestion des Produits</h1>

        {/* BARRE D'ACTIONS */}
        <div className={styles.toolbar}>
          <div className={styles.toolbarSection}>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={styles.select}
            >
              <option value="">Choisir tri...</option>
              <option value="name-asc">Nom (A → Z)</option>
              <option value="name-desc">Nom (Z → A)</option>
              <option value="stock-asc">Stock (croissant)</option>
              <option value="stock-desc">Stock (décroissant)</option>
            </select>
            <button onClick={handleSort} className={styles.btn} title="Applique le tri sélectionné à la liste des produits">
              Trier
            </button>
          </div>

          <div className={styles.toolbarSection}>
            <button onClick={handleAdd} className={styles.btnAdd} title="Créer un nouveau produit">
              Ajouter
            </button>
            <button onClick={handleSave} className={checkedIds.size > 0 ? styles.btnSaveActive : styles.btnSave}
              title={checkedIds.size > 0 ? `Enregistre uniquement les ${checkedIds.size} produit(s) cochés` : 'Enregistre toutes les modifications en base de données'}>
              {checkedIds.size > 0 ? `Enregistrer (${checkedIds.size})` : 'Enregistrer tout'}
            </button>
            {checkedIds.size > 0 && (
              <button onClick={() => setCheckedIds(new Set())} className={styles.btn} title="Décoche tous les produits sélectionnés">
                ✕ Désélectionner
              </button>
            )}
            <button
              onClick={handleEnrichir}
              className={styles.btnEnrich}
              title={checkedIds.size > 0 ? `Enrichir les ${checkedIds.size} produit(s) cochés via le site fournisseur` : 'Enrichir automatiquement les produits sans image valide via le site fournisseur'}
            >
              🔍 Enrichir{checkedIds.size > 0 ? ` (${checkedIds.size})` : ''}
            </button>
            <button onClick={handleExportCSV} className={styles.btn} title="Télécharge tous les produits au format CSV">
              Exporter CSV
            </button>
          </div>

          <div className={styles.toolbarSection}>
            <button onClick={handleCleanTitles} className={styles.btn} title='Supprime "Default Title" et les noms de variantes en fin de nom produit (ex: "Produit - 50ml" → "Produit")'>
              Nettoyer titres
            </button>
            <button onClick={handleUpdateStock} className={styles.btn} title="Recalcule le stock de chaque produit en additionnant le stock de toutes ses variantes">
              MAJ Stock
            </button>
            <button onClick={handleAutoCategorize} className={styles.btn} title="Catégorise automatiquement selon les mots-clés dans le nom du produit">
              🏷️ Auto-catégoriser{checkedIds.size > 0 ? ` (${checkedIds.size})` : ''}
            </button>
            <button onClick={handleCleanDescriptions} className={styles.btn} title="Supprime les balises HTML (<p>, <br>...), les \n littéraux et décode les entités (&amp;, &nbsp;...) dans les descriptions. Coche automatiquement les produits modifiés pour pouvoir les enregistrer.">
              🧹 Nettoyer descriptions{checkedIds.size > 0 ? ` (${checkedIds.size})` : ''}
            </button>
          </div>

          <div className={styles.toolbarSection}>
            <button
              onClick={() => { setShowApiSettings((v) => !v); setEditingApiUrl(false); }}
              className={styles.btn}
              title="Paramètres URL API"
            >
              ⚙️ API
            </button>
          </div>

        </div>

        {/* PARAMÈTRES URL API */}
        {showApiSettings && (
          <div className={styles.apiSettingsPanel}>
            <div className={styles.apiSettingsHeader}>
              <span className={styles.apiSettingsTitle}>⚙️ URL de base de l'API</span>
              <button onClick={() => setShowApiSettings(false)} className={styles.apiSettingsClose}>✕</button>
            </div>
            <div className={styles.apiSettingsBody}>
              <div className={styles.apiCurrentRow}>
                <span className={styles.apiCurrentLabel}>URL actuelle :</span>
                <code className={styles.apiCurrentValue}>{currentApiUrl}</code>
                {!editingApiUrl && (
                  <button
                    onClick={() => { setEditingApiUrl(true); setNewApiUrl(currentApiUrl); }}
                    className={styles.btn}
                  >
                    Modifier
                  </button>
                )}
                {currentApiUrl !== DEFAULT_API_URL && !editingApiUrl && (
                  <button
                    onClick={() => {
                      resetApiBaseUrl();
                      apiService.updateBaseUrl(DEFAULT_API_URL);
                      setCurrentApiUrl(DEFAULT_API_URL);
                      addLog(`↩️ URL API réinitialisée : ${DEFAULT_API_URL}`);
                    }}
                    className={styles.btn}
                    title="Revenir à l'URL par défaut"
                  >
                    Réinitialiser
                  </button>
                )}
              </div>
              {editingApiUrl && (
                <div className={styles.apiEditRow}>
                  <div className={styles.apiOldUrl}>Ancienne URL : <code>{currentApiUrl}</code></div>
                  <input
                    type="text"
                    value={newApiUrl}
                    onChange={(e) => setNewApiUrl(e.target.value)}
                    placeholder="https://mon-api.com/api/"
                    className={styles.apiInput}
                    autoFocus
                  />
                  <div className={styles.apiEditActions}>
                    <button onClick={handleSaveApiUrl} className={styles.btnSave}>
                      Sauvegarder
                    </button>
                    <button onClick={() => { setEditingApiUrl(false); setNewApiUrl(''); }} className={styles.btn}>
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* BARRE DE FILTRE RAPIDE */}
        <div className={styles.filterBar}>
          <input
            type="text"
            placeholder="Rechercher par nom, catégorie, marque..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            className={styles.filterInput}
          />
          <select
            value={filterCategory}
            onChange={(e) => { setFilterCategory(e.target.value); setFilterSubCategory(''); }}
            className={styles.select}
          >
            <option value="">Toutes catégories</option>
            {categoryList.map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
          {filterCategory && subCategoryList.length > 0 && (
            <select
              value={filterSubCategory}
              onChange={(e) => setFilterSubCategory(e.target.value)}
              className={styles.select}
            >
              <option value="">Toutes sous-catégories</option>
              {subCategoryList.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
          <select
            value={filterImage}
            onChange={(e) => setFilterImage(e.target.value)}
            className={styles.select}
          >
            <option value="">Toutes les images</option>
            <option value="no-image">Sans image</option>
            <option value="invalid-image">Image invalide</option>
          </select>
          <select
            value={filterDesc}
            onChange={(e) => setFilterDesc(e.target.value)}
            className={styles.select}
          >
            <option value="">Toutes descriptions</option>
            <option value="has-tags">Descriptions avec balises</option>
          </select>
          <input
            type="text"
            placeholder="Mot dans la description..."
            value={filterDescQuery}
            onChange={(e) => setFilterDescQuery(e.target.value)}
            className={styles.filterInput}
            style={{ maxWidth: '200px' }}
          />
          <label className={styles.filterCheckLabel} title={`${duplicateIds.size} produit(s) avec un doublon potentiel détecté`}>
            <input type="checkbox" checked={filterDuplicates} onChange={e => setFilterDuplicates(e.target.checked)} />
            Doublons {duplicateIds.size > 0 && <span className={styles.duplicateBadge}>{duplicateIds.size}</span>}
          </label>
          <label className={styles.filterCheckLabel}>
            <input type="checkbox" checked={filterMeta} onChange={e => setFilterMeta(e.target.checked)} />
            Sans meta desc.
          </label>
          <label className={styles.filterCheckLabel}>
            <input type="checkbox" checked={filterNoCategory} onChange={e => setFilterNoCategory(e.target.checked)} />
            Sans catégorie
          </label>
          <label className={styles.filterCheckLabel}>
            <input type="checkbox" checked={filterNoSubCategory} onChange={e => setFilterNoSubCategory(e.target.checked)} />
            Sans sous-cat.
          </label>
          {knownBrands.length > 0 && (
            <select
              value={filterBrand}
              onChange={(e) => setFilterBrand(e.target.value)}
              className={styles.select}
            >
              <option value="">Toutes marques</option>
              {knownBrands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          )}
          <span className={styles.filterCount}>
            {filteredProducts.length} / {products.length} produit{products.length > 1 ? 's' : ''}
          </span>
          {(filterQuery || filterCategory || filterSubCategory || filterImage || filterDesc || filterMeta || filterNoCategory || filterNoSubCategory || filterBrand || filterDescQuery || filterDuplicates) && (
            <button onClick={() => {
              setFilterQuery(''); setFilterCategory(''); setFilterSubCategory('');
              setFilterImage(''); setFilterDesc(''); setFilterMeta(false);
              setFilterNoCategory(false); setFilterNoSubCategory(false); setFilterBrand(''); setFilterDescQuery(''); setFilterDuplicates(false);
              ['pf_query','pf_category','pf_subCategory','pf_image','pf_desc','pf_meta','pf_noCat','pf_noSubCat','pf_brand','pf_descQuery','pf_duplicates'].forEach(k => sessionStorage.removeItem(k));
            }} className={styles.btn}>
              ✕ Effacer
            </button>
          )}
        </div>

        {/* RECHERCHE/REMPLACEMENT */}
        <div className={styles.searchSection}>
          <input
            type="text"
            placeholder="Texte à rechercher"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className={styles.input}
          />
          <input
            type="text"
            placeholder="Remplacer par"
            value={replaceText}
            onChange={(e) => setReplaceText(e.target.value)}
            className={styles.input}
          />
          <button onClick={handleSearchReplacePreview} className={styles.btn}>
            Rechercher
          </button>
          <button onClick={handleSearchReplaceApply} className={styles.btnSave}>
            Appliquer
          </button>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {loading ? (
          <div className={styles.loading}>Chargement des produits...</div>
        ) : (
          <>
            {/* TABLEAU PRODUITS */}
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thCheck}>
                      <input
                        type="checkbox"
                        checked={allVisibleChecked}
                        onChange={handleToggleAll}
                        title="Tout sélectionner / désélectionner"
                      />
                    </th>
                    <th>Nom</th>
                    <th>Catégorie</th>
                    <th>Sous-catégorie</th>
                    <th>Marque</th>
                    <th>Prix</th>
                    <th>Prix remisé</th>
                    <th>Stock</th>
                    <th>BestSeller</th>
                    <th>BonneAffaire</th>
                    <th>Variants</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr
                      key={product.id}
                      onClick={() => handleProductClick(product)}
                      onDoubleClick={() => navigate(`/produits/${product.id}`)}
                      className={`${styles.row} ${
                        selectedProductId === product.id ? styles.selectedRow : ''
                      } ${checkedIds.has(product.id) ? styles.checkedRow : ''}`}
                      title="Double-cliquez pour modifier"
                    >
                      <td className={styles.tdCheck} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={checkedIds.has(product.id)}
                          onChange={() => handleToggleCheck(product.id)}
                        />
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <input
                            type="text"
                            value={product.name || ''}
                            onChange={(e) =>
                              handleFieldChange(product.id, 'name', e.target.value)
                            }
                            className={styles.cellInput}
                            onClick={(e) => e.stopPropagation()}
                          />
                          {duplicateIds.has(product.id) && (
                            <span
                              className={styles.duplicateTag}
                              title={`Doublon potentiel avec : ${(duplicateGroups.get(product.id) ?? []).map(id => products.find(p => p.id === id)?.name ?? id).join(', ')}`}
                            >
                              ⚠ doublon
                              <button
                                className={styles.duplicateIgnoreBtn}
                                title="Confirmer que ce produit n'est pas un doublon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIgnoredDuplicateIds(prev => new Set([...prev, product.id]));
                                }}
                              >✕</button>
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <select
                          value={product.category || ''}
                          onChange={(e) => {
                            handleFieldChange(product.id, 'category', e.target.value);
                            handleFieldChange(product.id, 'subCategory', '');
                          }}
                          className={styles.cellSelect}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">—</option>
                          {categoryList.map((c) => (
                            <option key={c.id} value={c.name}>{c.name}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          value={product.subCategory || ''}
                          onChange={(e) =>
                            handleFieldChange(product.id, 'subCategory', e.target.value)
                          }
                          className={styles.cellSelect}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">—</option>
                          {(categoryList.find((c) => c.name === product.category)?.subCategories ?? []).map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          value={product.brand || ''}
                          onChange={(e) => handleFieldChange(product.id, 'brand', e.target.value)}
                          className={styles.cellInput}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Marque"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={product.price || 0}
                          onChange={(e) =>
                            handleFieldChange(
                              product.id,
                              'price',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className={styles.cellInput}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={product.discountPrice || ''}
                          onChange={(e) =>
                            handleFieldChange(
                              product.id,
                              'discountPrice',
                              e.target.value ? parseFloat(e.target.value) : null
                            )
                          }
                          className={styles.cellInput}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={product.stock}
                          onChange={(e) =>
                            handleFieldChange(
                              product.id,
                              'stock',
                              parseInt(e.target.value) || 0
                            )
                          }
                          className={styles.cellInput}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={product.bestSeller || false}
                          onChange={(e) =>
                            handleFieldChange(
                              product.id,
                              'bestSeller',
                              e.target.checked
                            )
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={product.bonneAffaire || false}
                          onChange={(e) =>
                            handleFieldChange(
                              product.id,
                              'bonneAffaire',
                              e.target.checked
                            )
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td>{getVariantCount(product.id)}</td>
                      <td>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(product.id);
                          }}
                          className={styles.btnDelete}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* TABLEAU VARIANTES */}
            {selectedProductId !== null && (
              <div className={styles.variantsSection}>
                <h2 className={styles.variantsTitle}>
                  Variantes du produit #{selectedProductId}
                </h2>
                {selectedVariants.length === 0 ? (
                  <p className={styles.noData}>Aucune variante pour ce produit.</p>
                ) : (
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Nom</th>
                        <th>Prix</th>
                        <th>Stock</th>
                        <th>SKU</th>
                        <th>Option1</th>
                        <th>Option2</th>
                        <th>Option3</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedVariants.map((variant) => (
                        <tr key={variant.id}>
                          <td>{variant.name}</td>
                          <td>{variant.price}</td>
                          <td>{variant.stock}</td>
                          <td>{variant.sku || '-'}</td>
                          <td>{variant.option1Value || '-'}</td>
                          <td>{variant.option2Value || '-'}</td>
                          <td>{variant.option3Value || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* PREVIEW RECHERCHE/REMPLACEMENT */}
            {replacePreview.length > 0 && (
              <div className={styles.previewSection}>
                <h2 className={styles.previewTitle}>
                  Aperçu remplacement ({replacePreview.length} éléments)
                </h2>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Id</th>
                      <th>Original</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {replacePreview.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.Type}</td>
                        <td>{item.Id}</td>
                        <td>{item.Original}</td>
                        <td>{item.Updated}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* LOGS */}
            <div className={styles.logSection}>
              <h2 className={styles.logTitle}>Logs</h2>
              <div className={styles.logContainer}>
                {logs.map((log, idx) => (
                  <div key={idx} className={styles.logLine}>
                    {log}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};
